import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabaseClient';

const UserDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch leads from database
  useEffect(() => {
    const fetchLeads = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform leads data to match the display format
        const transformedLeads = (data || []).map(lead => ({
          id: lead.id,
          date: new Date(lead.created_at).toLocaleDateString(),
          from: {
            address: lead.origin_address,
            city: lead.origin_city,
            zip: lead.origin_postal_code,
            state: lead.origin_state
          },
          to: {
            address: lead.destination_address,
            city: lead.destination_city,
            zip: lead.destination_postal_code,
            state: lead.destination_state
          },
          category: lead.move_type,
          moveType: lead.assistance_type,
          property: lead.property_size || lead.piano_type || lead.truck_size || lead.vehicle_type,
          status: lead.status,
          selectedMoverIds: lead.selected_mover_ids || []
        }));

        setRequests(transformedLeads);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();

    // Set up real-time subscription only if user exists
    if (!user?.id) return;

    const subscription = supabase
      .channel(`user_leads_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          fetchLeads();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t === 'requests') setTab(t);
  }, [location.search]);

  const setTabAndUrl = (t) => {
    setTab(t);
    const params = new URLSearchParams(location.search);
    params.set('tab', t);
    navigate({ pathname: '/user', search: params.toString() }, { replace: true });
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-yellow-50/40 py-8">
      <Helmet>
        <title>My Dashboard – MyMoveAdvisor</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_6px_24px_rgba(0,0,0,0.08)]">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div>
              <div className="text-xl font-bold text-gray-800">User Dashboard</div>
              <div className="text-sm text-gray-500">Manage your quote requests and explore movers</div>
            </div>
            <Link
              to="/quote"
              className="px-4 py-2 bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg text-sm font-semibold hover:shadow-md transition-all inline-flex items-center gap-2"
            >
              <span>+</span>
              <span>Create New Quote</span>
            </Link>
          </div>
          <div className="px-5 border-b">
            <div className="flex gap-6 overflow-x-auto">
              <button onClick={() => setTabAndUrl('requests')} className={`py-3 border-b-2 -mb-px font-medium ${tab === 'requests' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>My Requests</button>
            </div>
          </div>

          <div className="p-5">
            {tab === 'requests' && (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl p-4 border border-yellow-200 shadow-sm"><div className="text-sm text-gray-600">Total Requests</div><div className="text-2xl font-bold">{requests.length}</div></div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"><div className="text-sm text-gray-600">Pending</div><div className="text-2xl font-bold">{requests.filter(r => r.status !== 'Completed').length}</div></div>
                  <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm"><div className="text-sm text-gray-600">Completed</div><div className="text-2xl font-bold">{requests.filter(r => r.status === 'Completed').length}</div></div>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                    <p className="text-gray-600 mt-4">Loading your requests...</p>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="bg-white rounded-xl p-6 border text-center text-gray-600">No requests yet. Start by creating a <Link className="text-blue-600 hover:underline" to="/quote">new quote</Link>.</div>
                ) : (
                  <div className="space-y-3">
                    {requests.map(r => (
                      <div key={r.id} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="font-semibold text-gray-800">Request #{r.id.substring(0, 8)}</div>
                          <div className="text-sm text-gray-600">{r.date}</div>
                        </div>
                        <div className="text-sm text-gray-600 mt-2">
                          {(r.from?.city || r.from?.zip || r.from?.address) ? `${[r.from?.address, r.from?.city, r.from?.zip].filter(Boolean).join(', ')}` : r.from}
                          {" → "}
                          {(r.to?.city || r.to?.zip || r.to?.address) ? `${[r.to?.address, r.to?.city, r.to?.zip].filter(Boolean).join(', ')}` : r.to}
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {r.category && <span className="px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">{r.category}</span>}
                          {r.moveType && <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">{r.moveType}</span>}
                          {r.property && <span className="px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-700 border">{r.property}</span>}
                          {r.status && <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'Pending' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : r.status === 'Completed' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-700 border'}`}>{r.status}</span>}
                          <span className="ml-auto" />
                          <button
                            onClick={() => navigate('/user/view-movers', { state: { quote: r } })}
                            className="px-3 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-50"
                          >
                            View Movers ({r.selectedMoverIds?.length || 0})
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
